import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { formValueSelector, reduxForm } from 'redux-form'

import { getApp } from 'ducks/apps'
import { createTable, getDatasources } from 'ducks/apps/datasources'
import { getCurrentOrganization, isFeatureEnabled } from 'ducks/organizations'
import { useFunnelTracking } from 'hooks/useFunnelTracking'

import Modal from 'components/Shared/Modal'
import Button from 'components/Shared/Button'

import history from '../../../history'

import useSheetBridgeFlow, { LOADING } from './useSheetBridgeFlow'
import { createCollections } from './createCollections'
import { SheetBridgeAuthStep } from './SheetBridgeAuthStep'
import { SheetBridgeLogo } from './SheetBridgeLogo'
import SheetBridgeBridgesForm from './SheetBridgeBridgesForm'
import './index.scss'

export const FORM_NAME = 'sheetBridgeSetup'

const SheetBridgeModal = props => {
  const dispatch = useDispatch()
  const { match } = props
  const { appId } = match.params

  const app = useSelector(state => getApp(state, appId))
  const datasources = useSelector(state =>
    app ? getDatasources(state, app.id) : undefined
  )
  const datasource = datasources?.[0]

  const currentOrganization = useSelector(getCurrentOrganization)
  const { trackSheetBridgeAddedCollections } = useFunnelTracking()

  const hasAccess = useSelector(state =>
    isFeatureEnabled(state, 'customIntegrations')
  )

  const selector = formValueSelector(FORM_NAME)
  const formValueBridges =
    useSelector(state => selector(state, 'bridges')) || {}

  const [isLoading, setIsLoading] = useState(false)

  const {
    step,
    bridges,
    bridgesStatus,
    authState,
    checkingAuth,
    noBridges,
    handleGetBridges,
    goBack,
  } = useSheetBridgeFlow({
    appId: app?.id,
    organizationId: currentOrganization?.id,
  })

  useEffect(() => {
    if (hasAccess === false) history.push(`/apps/${appId}/data`)
  }, [hasAccess, appId])

  if (hasAccess === false) return null

  const handleClose = () => history.push(`/apps/${appId}/data`)

  const handleSubmit = async () => {
    const selectedSheetKeys = Object.keys(formValueBridges).filter(
      key => formValueBridges[key]
    )
    if (!selectedSheetKeys.length) return

    setIsLoading(true)
    try {
      await createCollections({
        selectedSheetKeys,
        bridges,
        dispatch: tableData =>
          dispatch(createTable(app.id, datasource.id, tableData)),
      })

      if (currentOrganization?.id) {
        trackSheetBridgeAddedCollections(currentOrganization.id).catch(
          error => {
            console.error(
              'Failed to track SheetBridge added collections:',
              error
            )
          }
        )
      }

      handleClose()
    } catch (err) {
      console.error('Failed to create collections:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const canProceed =
    bridgesStatus === 'loaded' && Object.values(formValueBridges).some(Boolean)

  const renderContent = () => {
    switch (step) {
      case 0:
        return (
          <SheetBridgeAuthStep
            checkingAuth={checkingAuth}
            authState={authState}
            noBridges={noBridges}
            onGetBridges={handleGetBridges}
          />
        )
      case 1:
        return (
          <SheetBridgeBridgesForm
            bridges={bridges}
            status={bridgesStatus}
            datasource={datasource}
          />
        )
      default:
        return null
    }
  }

  return (
    <Modal size="lg" scrolling className="sheetbridge-modal">
      <div>
        <Modal.Header>
          <SheetBridgeLogo />
        </Modal.Header>
        <Modal.Content>{renderContent()}</Modal.Content>
        <Modal.Actions
          leftButtons={[
            <Button
              key="back"
              text
              onClick={step === 0 ? handleClose : goBack}
              type="button"
              disabled={isLoading || authState === LOADING}
            >
              {step === 0 ? 'Close' : 'Back'}
            </Button>,
          ]}
        >
          {step === 1 && (
            <Button
              teal
              type="button"
              disabled={!canProceed || isLoading}
              onClick={handleSubmit}
              loading={isLoading}
            >
              Connect
            </Button>
          )}
        </Modal.Actions>
      </div>
    </Modal>
  )
}

export default reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  initialValues: {},
})(SheetBridgeModal)
